import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { AgencyProvider, useAgency } from './lib/AgencyContext';
import { I18nProvider } from './lib/i18n';

import LoginPage from './components/LoginPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import ResetPassword from './components/ResetPassword';
import AppLayout from './components/AppLayout';
import AgencyPicker from './components/AgencyPicker';
import OnboardingPage from './components/OnboardingPage';

import Dashboard from './components/Dashboard';
import InvoicingList from './components/InvoicingList';
import InvoiceForm from './components/InvoiceForm';
import InvoicePrintView from './components/InvoicePrintView';
import Taxes from './components/Taxes';
import ClientsList from './components/ClientsList';
import ClientForm from './components/ClientForm';
import ClientDetail from './components/ClientDetail';
import Products from './components/Products';
import KpoBook from './components/KpoBook';
import ExchangeList from './components/ExchangeList';
import TaxCalendar from './components/TaxCalendar';
import UserProfile from './components/UserProfile';
import NotificationSettings from './components/NotificationSettings';
import SupportPage from './components/SupportPage';
import AgencySettings from './components/AgencySettings';
import Referrals from './components/Referrals';
import FiscalComingSoon from './components/FiscalComingSoon';
import EFakturePage from './components/EFakturePage';
import DailyTakings from './components/DailyTakings';
import EFaktureGuide from './components/EFaktureGuide';

import './styles/tokens.css';

const loadingScreen = (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-500)' }}>Učitavanje...</div>
);

function Gate() {
  const { user, loading: authLoading } = useAuth();
  const { agencies, activeAgencyId, loading: agencyLoading, profile, profileLoading } = useAgency();

  if (authLoading) return loadingScreen;

  // Ruta za reset lozinke mora biti dostupna i kad nije ulogovan
  if (window.location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  if (!user) return <LoginPage />;

  if (!user.email_confirmed_at && !user.confirmed_at) {
    return <VerifyEmailPage />;
  }

  if (agencyLoading) return loadingScreen;

  // Novi korisnik bez ijedne firme — dobrodošlica i unos podataka firme
  if (!activeAgencyId && agencies.length === 0) return <OnboardingPage mode="first" />;

  // Nije izabrana agencija — prikaži listu agencija (izbor postojeće ili dodavanje nove)
  if (!activeAgencyId) return <AgencyPicker />;

  // Čekamo profil samo pri prvom učitavanju agencije. Kad se profil kasnije
  // osvežava (npr. posle čuvanja na stranici Agencija), stranica ostaje prikazana.
  if (profileLoading && !profile) return loadingScreen;

  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<Dashboard />} />

        <Route path="/fakturisanje" element={<InvoicingList />} />
        <Route path="/fakturisanje/nova" element={<InvoiceForm />} />
        <Route path="/fakturisanje/nova-profaktura" element={<InvoiceForm forceKind="profaktura" />} />
        <Route path="/fakturisanje/:id/pregled" element={<InvoicePrintView />} />
        <Route path="/fakturisanje/:id/izmeni" element={<InvoiceForm />} />

        {/* Fiskalna kasa je u izradi — za sada samo stranica "U izradi" */}
        <Route path="/fiskalna-kasa" element={<FiscalComingSoon />} />
        <Route path="/avansi" element={<FiscalComingSoon variant="avans" />} />
        <Route path="/efakture" element={<EFakturePage />} />
        <Route path="/pazar" element={<DailyTakings />} />
        <Route path="/efakture/uputstvo" element={<EFaktureGuide />} />

        <Route path="/porezi" element={<Taxes />} />

        {/* Klijenti (kupci kojima se izdaju dokumenti) */}
        <Route path="/klijenti" element={<ClientsList />} />
        <Route path="/klijenti/novi" element={<ClientForm />} />
        <Route path="/klijenti/:id" element={<ClientDetail />} />
        <Route path="/klijenti/:id/izmeni" element={<ClientForm />} />

        <Route path="/proizvodi" element={<Products />} />
        <Route path="/kpo-knjiga" element={<KpoBook />} />
        <Route path="/kursna-lista" element={<ExchangeList />} />
        <Route path="/poreski-kalendar" element={<TaxCalendar />} />

        <Route path="/profil" element={<UserProfile />} />
        <Route path="/podesavanja/notifikacije" element={<NotificationSettings />} />
        <Route path="/podrska" element={<SupportPage />} />
        <Route path="/podrska/:tip" element={<SupportPage />} />
        <Route path="/agencija" element={<AgencySettings />} />
        <Route path="/preporuke" element={<Referrals />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <AgencyProvider>
            <Gate />
          </AgencyProvider>
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}
